import React, { useState, useEffect } from "react";
import { 
  Plus, Search, Truck, AlertTriangle, Droplet, FileDown, Wrench, ClipboardList, ArrowUp, ClipboardCheck, Fuel, Package 
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatsCard } from "@/components/layout";
import { VehicleFormModal, VehicleDetailModal, VehicleRowCard } from "@/components/vehicles";
import { ImportExportModal } from "@/components/settings";
import { FuelReportDialog } from "./FuelReportDialog";
import { PartsRequestDialog } from "./PartsRequestDialog";
import { hasPermission, PERMISSIONS } from "@/utils/permissions";

// Simple vehicle list component - no virtualization, just a long scrollable page
const VehicleList = ({ vehicles, loading, serviceIntervals, onVehicleClick }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-500">
        Loading vehicles...
      </div>
    );
  }

  if (vehicles.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-500">
        <Truck className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>No vehicles found</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-2">
      {vehicles.map((vehicle) => (
        <VehicleRowCard
          key={vehicle.id}
          vehicle={vehicle}
          onClick={() => onVehicleClick(vehicle)}
          serviceIntervals={serviceIntervals}
          style={{}}
        />
      ))}
      <div className="text-center py-4 text-xs text-slate-400">
        {vehicles.length} vehicles
      </div>
    </div>
  );
};

// Scroll to top button component
const ScrollToTopButton = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      // Show button when page is scrolled down 400px
      if (window.scrollY > 400) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  if (!isVisible) return null;

  return (
    <button
      onClick={scrollToTop}
      className="fixed bottom-6 right-6 z-50 w-12 h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 active:scale-95"
      style={{ bottom: 'max(1.5rem, calc(env(safe-area-inset-bottom) + 0.5rem))' }}
      data-testid="btn-scroll-to-top"
      aria-label="Scroll to top"
    >
      <ArrowUp className="w-5 h-5" />
    </button>
  );
};

export const Assets = ({ vehicles, stats, serviceIntervals, onUpdate, loading, onNavigateToServiceRequest, onNavigateToDVIR, currentUser }) => {
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedVehicleTab, setSelectedVehicleTab] = useState("details");
  const [showImportExport, setShowImportExport] = useState(false);
  const [showRecordServiceSelect, setShowRecordServiceSelect] = useState(false);
  const [showFuelReport, setShowFuelReport] = useState(false);
  const [showPartsRequest, setShowPartsRequest] = useState(false);
  
  const userRole = currentUser?.role;
  
  // Permission checks
  const canAddVehicle = hasPermission(userRole, PERMISSIONS.ASSETS.ADD_VEHICLE);
  const canImportExport = hasPermission(userRole, PERMISSIONS.ASSETS.IMPORT_EXPORT);
  const canReportService = hasPermission(userRole, PERMISSIONS.ASSETS.REPORT_SERVICE);
  const canServiceRequest = hasPermission(userRole, PERMISSIONS.ASSETS.SERVICE_REQUEST);
  const canCreateDVIR = hasPermission(userRole, PERMISSIONS.ASSETS.CREATE_DVIR);
  const canAccessParts = hasPermission(userRole, PERMISSIONS.NAV.PARTS);

  const openVehicleDetails = (vehicle) => {
    setSelectedVehicleTab("details");
    setSelectedVehicle(vehicle);
  };

  // Keep selectedVehicle in sync with refreshed vehicles data
  useEffect(() => {
    if (selectedVehicle && vehicles.length > 0) {
      const updatedVehicle = vehicles.find(v => v.id === selectedVehicle.id);
      if (updatedVehicle && JSON.stringify(updatedVehicle) !== JSON.stringify(selectedVehicle)) {
        setSelectedVehicle(updatedVehicle);
      }
    }
  }, [vehicles, selectedVehicle]);

  const openRecordServiceForVehicle = (vehicleId) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    if (vehicle) {
      setSelectedVehicleTab("service");
      setSelectedVehicle(vehicle);
      setShowRecordServiceSelect(false);
    }
  };

  const filteredVehicles = vehicles.filter((v) => {
    const matchesFilter = filter === "all" || v.vehicle_type === filter;
    const query = searchQuery.toLowerCase();
    const matchesSearch = 
      v.unit_number.toLowerCase().includes(query) ||
      v.vin.toLowerCase().includes(query) ||
      v.make.toLowerCase().includes(query) ||
      v.model.toLowerCase().includes(query) ||
      (v.year && v.year.toString().includes(query)) ||
      (v.vehicle_type && v.vehicle_type.replace('_', ' ').toLowerCase().includes(query)) ||
      (v.fuel_type && v.fuel_type.toLowerCase().includes(query)) ||
      (v.transmission_type && v.transmission_type.toLowerCase().includes(query)) ||
      (v.transmission_make && v.transmission_make.toLowerCase().includes(query)) ||
      (v.transmission_model && v.transmission_model.toLowerCase().includes(query)) ||
      (v.engine_make && v.engine_make.toLowerCase().includes(query)) ||
      (v.engine_model && v.engine_model.toLowerCase().includes(query)) ||
      (v.engine_serial && v.engine_serial.toLowerCase().includes(query)) ||
      (v.is_out_of_service && "out of service".includes(query)) ||
      (v.out_of_service_reason && v.out_of_service_reason.toLowerCase().includes(query));
    return matchesFilter && matchesSearch;
  });

  return (
    <div className="p-4 sm:p-6 lg:p-8 overflow-x-hidden pt-[145px] sm:pt-6 lg:pt-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Fleet Overview</h1>
          <p className="text-sm sm:text-base text-slate-500 mt-1">Manage your vehicles and maintenance schedules</p>
        </div>
        {/* Desktop buttons */}
        <div className="hidden sm:flex gap-3 flex-wrap">
          {canImportExport && (
            <Button variant="outline" onClick={() => setShowImportExport(true)} data-testid="btn-import-export">
              <FileDown className="w-4 h-4 mr-2" /> Import / Export
            </Button>
          )}
          {canReportService && (
            <Button onClick={() => setShowFuelReport(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="btn-fuel-report">
              <Fuel className="w-4 h-4 mr-2" /> Fuel Report
            </Button>
          )}
          {canAccessParts && (
            <Button onClick={() => setShowPartsRequest(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="btn-parts-request">
              <Package className="w-4 h-4 mr-2" /> Parts Request
            </Button>
          )}
          {canReportService && (
            <Button onClick={() => setShowRecordServiceSelect(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="btn-record-service">
              <Wrench className="w-4 h-4 mr-2" /> Report Service or Repair
            </Button>
          )}
          {canServiceRequest && (
            <Button onClick={onNavigateToServiceRequest} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="btn-service-request">
              <ClipboardList className="w-4 h-4 mr-2" /> Service Request
            </Button>
          )}
          {canAddVehicle && (
            <Button variant="outline" onClick={() => setShowAddModal(true)} data-testid="btn-add-vehicle">
              <Plus className="w-4 h-4 mr-2" /> Add Vehicle
            </Button>
          )}
        </div>
        {/* Mobile buttons - full width stacked */}
        <div className="flex sm:hidden flex-col gap-2 w-full">
          {canReportService && (
            <Button onClick={() => setShowFuelReport(true)} className="w-full justify-center h-11 bg-blue-600 hover:bg-blue-700 text-white" data-testid="btn-fuel-report-mobile">
              <Fuel className="w-4 h-4 mr-2" /> Fuel Report
            </Button>
          )}
          {canAccessParts && (
            <Button onClick={() => setShowPartsRequest(true)} className="w-full justify-center h-11 bg-blue-600 hover:bg-blue-700 text-white" data-testid="btn-parts-request-mobile">
              <Package className="w-4 h-4 mr-2" /> Parts Request
            </Button>
          )}
          {canReportService && (
            <Button onClick={() => setShowRecordServiceSelect(true)} className="w-full justify-center h-11 bg-blue-600 hover:bg-blue-700 text-white" data-testid="btn-record-service-mobile">
              <Wrench className="w-4 h-4 mr-2" /> Report Service or Repair
            </Button>
          )}
          {canServiceRequest && (
            <Button onClick={onNavigateToServiceRequest} className="w-full justify-center h-11 bg-blue-600 hover:bg-blue-700 text-white" data-testid="btn-service-request-mobile">
              <ClipboardList className="w-4 h-4 mr-2" /> Service Request
            </Button>
          )}
          {canCreateDVIR && (
            <Button onClick={onNavigateToDVIR} className="w-full justify-center h-11 bg-blue-600 hover:bg-blue-700 text-white" data-testid="btn-create-dvir-mobile">
              <ClipboardCheck className="w-4 h-4 mr-2" /> Create New DVIR
            </Button>
          )}
          {(canAddVehicle || canImportExport) && (
            <div className="grid grid-cols-2 gap-2">
              {canAddVehicle && (
                <Button variant="outline" onClick={() => setShowAddModal(true)} className="w-full justify-center h-11 text-xs px-2" data-testid="btn-add-vehicle-mobile">
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              )}
              {canImportExport && (
                <Button variant="outline" onClick={() => setShowImportExport(true)} className="w-full justify-center h-11 text-xs px-2" data-testid="btn-import-export-mobile">
                  <FileDown className="w-4 h-4 mr-1" /> Import/Export
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid mb-6">
        <StatsCard icon={Truck} value={stats.tractors} label="Tractors" color="#007AFF" bgColor="rgba(0, 122, 255, 0.1)" oosCount={stats.tractors_oos} />
        <StatsCard icon={Truck} value={stats.chassis} label="Chassis" color="#34C759" bgColor="rgba(52, 199, 89, 0.1)" oosCount={stats.chassis_oos} />
        <StatsCard icon={Truck} value={stats.yard_equipment} label="Yard Equip." color="#FF9500" bgColor="rgba(255, 149, 0, 0.1)" oosCount={stats.yard_equipment_oos} />
        <StatsCard icon={AlertTriangle} value={stats.needs_bit} label="Needs B.I.T." color="#FF3B30" bgColor="rgba(255, 59, 48, 0.1)" />
        <StatsCard icon={AlertTriangle} value={stats.needs_dot} label="Needs D.O.T." color="#FF3B30" bgColor="rgba(255, 59, 48, 0.1)" />
        <StatsCard icon={Droplet} value={stats.needs_oil} label="Needs Oil" color="#FF3B30" bgColor="rgba(255, 59, 48, 0.1)" />
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1 sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search vehicles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="input-search"
          />
        </div>
        <div className="filter-tabs overflow-x-auto">
          {[
            { id: "all", label: "All" },
            { id: "tractor", label: "Tractors" },
            { id: "chassis", label: "Chassis" },
            { id: "yard_equipment", label: "Yard Equipment" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              className={`filter-tab ${filter === tab.id ? "active" : ""}`}
              data-testid={`filter-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Vehicle List */}
      <VehicleList
        vehicles={filteredVehicles}
        loading={loading}
        serviceIntervals={serviceIntervals}
        onVehicleClick={openVehicleDetails}
      />

      {/* Scroll to Top Button */}
      <ScrollToTopButton />

      {/* Modals */}
      <VehicleFormModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        vehicle={null}
        onSave={onUpdate}
      />

      <VehicleDetailModal
        open={!!selectedVehicle}
        onClose={() => setSelectedVehicle(null)}
        vehicle={selectedVehicle}
        onUpdate={async () => { 
          await onUpdate(); 
          // Keep modal open - the vehicle data will be refreshed via the vehicles prop
        }}
        serviceIntervals={serviceIntervals}
        defaultTab={selectedVehicleTab}
        currentUser={currentUser}
      />

      <ImportExportModal
        open={showImportExport}
        onClose={() => setShowImportExport(false)}
        onUpdate={onUpdate}
        stats={stats}
      />

      {/* Report Service or Repair Vehicle Selection Dialog */}
      <Dialog open={showRecordServiceSelect} onOpenChange={setShowRecordServiceSelect}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none sm:max-w-md sm:h-auto sm:max-h-[85vh] sm:rounded-lg sm:m-auto">
          <DialogHeader className="border-b pb-4 sm:border-0 sm:pb-0">
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-blue-600" />
              Report Service or Repair
            </DialogTitle>
          </DialogHeader>
          <div className="py-6 px-2 sm:py-4 sm:px-0">
            <Label className="mb-3 block text-base sm:text-sm">Select Vehicle</Label>
            <Select onValueChange={openRecordServiceForVehicle}>
              <SelectTrigger data-testid="select-service-vehicle" className="h-12 sm:h-10 text-base sm:text-sm">
                <SelectValue placeholder="Choose a vehicle..." />
              </SelectTrigger>
              <SelectContent className="max-h-[60vh] sm:max-h-[300px]">
                {vehicles.map((v) => (
                  <SelectItem key={v.id} value={v.id} className="py-3 sm:py-2">
                    Unit {v.unit_number} - {v.year} {v.make} {v.model}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-slate-500 mt-4">Select a vehicle to record a service entry.</p>
          </div>
          <div className="border-t pt-4 sm:hidden">
            <Button 
              variant="outline" 
              className="w-full h-12"
              onClick={() => setShowRecordServiceSelect(false)}
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Fuel Report Dialog */}
      <FuelReportDialog
        open={showFuelReport}
        onClose={() => setShowFuelReport(false)}
        vehicles={vehicles}
        currentUser={currentUser}
        onSuccess={onUpdate}
      />

      {/* Parts Request Dialog */}
      <PartsRequestDialog
        open={showPartsRequest}
        onClose={() => setShowPartsRequest(false)}
        vehicles={vehicles}
        currentUser={currentUser}
        onSuccess={onUpdate}
      />
    </div>
  );
};
